import { useEffect, useState } from 'react';
import { getBlob } from '../api/client';
import './PersonAvatar.css';

interface PersonAvatarProps {
  personId: number | null | undefined;
  /** Hint from the directory/person payload — skip the fetch when known false. */
  hasPhoto?: boolean;
  /** Name used for the initials fallback + alt text. */
  name?: string | null;
  size?: number;
  /** Bump to force a re-fetch when the photo bytes change but `hasPhoto`
   *  doesn't (e.g. replacing an existing photo, where it stays `true`). */
  refreshKey?: number | string;
}

// Honorific prefixes + name suffixes to skip so initials come from the actual
// first + last name ("Mr. Gregory Bernstein Esq." → "GB", "Dr. Jane Smith" → "JS").
const HONORIFICS = new Set(['mr', 'mrs', 'ms', 'miss', 'dr', 'prof', 'mx', 'sir', 'rev', 'hon', 'fr', 'br', 'st', 'capt', 'col', 'sgt', 'lt', 'gen', 'maj']);
const SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'esq', 'phd', 'md', 'jd', 'cpa', 'dds', 'dvm', 'rn', 'do', 'pe']);

function initialsOf(name: string | null | undefined): string {
  let tokens = (name ?? '').split(/\s+/).map((t) => t.replace(/\./g, '')).filter(Boolean);
  while (tokens.length > 1 && HONORIFICS.has(tokens[0]!.toLowerCase())) tokens = tokens.slice(1);
  while (tokens.length > 1 && SUFFIXES.has(tokens[tokens.length - 1]!.toLowerCase())) tokens = tokens.slice(0, -1);
  if (tokens.length === 0) return '?';
  const first = tokens[0]![0] ?? '';
  const last = tokens.length > 1 ? (tokens[tokens.length - 1]![0] ?? '') : '';
  return (first + last).toUpperCase() || '?';
}

/** Round contact avatar. Fetches GET /api/people/:id/photo (auth header) and
 *  renders an object URL; falls back to initials when there's no photo. */
export function PersonAvatar({ personId, hasPhoto, name, size = 36, refreshKey }: PersonAvatarProps): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    setUrl(null);
    if (!personId || hasPhoto === false) return;
    let cancelled = false;
    let objectUrl: string | null = null;
    void (async () => {
      try {
        const blob = await getBlob(`/api/people/${personId}/photo`);
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        /* 404 / no photo → initials fallback */
      }
    })();
    return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
  }, [personId, hasPhoto, refreshKey]);

  const dim = { width: size, height: size };
  if (url) {
    return <img src={url} alt={name ?? 'contact'} className="person-avatar" style={dim} />;
  }
  return (
    <span className="person-avatar person-avatar-initials" style={{ ...dim, fontSize: Math.round(size * 0.4) }}>
      {initialsOf(name)}
    </span>
  );
}
